// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, throwError } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { D3eService } from '../../../../services/d3e-service';
import { SelectedOrgMethod } from '../../../../shared/selectedOrg';
import { HeaderService } from '../../../header.service';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { IThreatsDetectedTotals } from '../../../../types/threat';
import { relativeChange } from '../../../../shared/misc';

@UntilDestroy({checkProperties: true})
@Component({
    selector: 'app-threats-detected',
    templateUrl: './threats-detected.component.html',
    styleUrls: ['./threats-detected.component.scss'],
    standalone: false
})
export class ThreatsDetectedComponent {
  oneWeek = '168h';
  threats$: Observable<IThreatsDetectedTotals>;
  loadingThreats$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  threatsLink = '/history';
  threatsQueryParams = {time: this.oneWeek};

  constructor(private d3eService: D3eService,
              private headerService: HeaderService) {

    this.threats$  = this.headerService.orgSelectedChanged
      .pipe(
        tap(() => {
          this.loadingThreats$.next(true);
        }),
        switchMap(selectedOrgId => {
          const selectedOrg = SelectedOrgMethod.getSelectedOrg(selectedOrgId);
          const oneWeekQueryJson = {
            'time': '168h',
            'deviceSet': null,
            'n': 0,
            'selectedOrgId': selectedOrg,
          };
          const twoWeeksQueryJson = {
            'time': '336h',
            'deviceSet': null,
            'n': 0,
            'selectedOrgId': selectedOrg,
          };
          return forkJoin({
            currentWeeksThreats: this.d3eService.getTopNThreats(oneWeekQueryJson),
            twoWeeksThreats: this.d3eService.getTopNThreats(twoWeeksQueryJson)
          })
        }),
        map(totals => {
          const currentWeekTotal = totals.currentWeeksThreats.reduce((partialCount, totals) => partialCount + totals.threat_count, 0);
          const twoWeeksTotal = totals.twoWeeksThreats.reduce((partialCount, totals) => partialCount + totals.threat_count, 0);
          const previousWeeksTotal = twoWeeksTotal - currentWeekTotal;
          const amountChanged = currentWeekTotal - previousWeeksTotal;
          const relativeDiff = relativeChange(previousWeeksTotal, currentWeekTotal);
          return {
            amountChanged: amountChanged,
            relativeChange: relativeDiff,
            threatsDetected: currentWeekTotal
          };
        }),
        tap(() => this.loadingThreats$.next(false)),
        catchError(error => {
          this.loadingThreats$.next(false);
          // TODO: pass error to child? so it can show error appropriately?
          //  Also, show a snack bar with error?
          return throwError(error);
        }),
        untilDestroyed(this)
      );

  }

}
